<script setup lang="ts">
import type { DataTableHeaders } from '@/plugins/vuetify'
import type MDBVegcoBucket from '@/types/vegco/bucket'
import { notifyError, to_wd_dd_MMMM_hh_mm, emptyMongoId, roundTo } from '@/utils/helpers'
import { getBucketBigType, unitésParBoîte } from '@/utils/helpers-vegco'



type MDBVegcoBucketExtended = MDBVegcoBucket & {
  producteur: string
  publié: boolean
  nomProduit: string
  typePalette: string
  unités: number
  dateChargéF: string
  noCommandeVente: string
  client: string
  dateChargé: Date
  nombrePalette: number
  unitsParPalette: number
}

const theme = useTheme()
const storeU = useUtilisateurStore()
const storeVegco = useVegcoStore()
const isLoading = ref(false)
const newData = ref(false)

const sites = storeU.utilisateur.pagesListeNoire.includes('vgco.tous') ?
  [storeU.siteSélectionné.lettresDappel] : ['Tous', 'DLFL', 'FHVW', 'PHVW', 'JPLG']
const selectedSite = ref(storeU.siteSélectionné.lettresDappel)
const selectedDay = ref(new Date(storeU.journéeSélectionnée))
const buckets = ref([] as MDBVegcoBucketExtended[])
const headers: DataTableHeaders = [
  { title: 'Producteur', key: 'producteur', width: '40px' },
  { title: 'Nom produit', key: 'nomProduit', width: '160px' },
  { title: 'Code de produit', key: 'codeProduit', width: '60px' },
  { title: 'Type de palette', key: 'typePalette', width: '60px' },
  { title: 'Nb de palettes', key: 'nombrePalette', width: '60px'},
  { title: 'Nb unités / palette', key: 'unitsParPalette', width: '60px'},
  { title: 'Unités', key: 'unités', width: '60px'},
  { title: 'Heure shipping MF', key: 'dateChargé', width: '120px'},
  { title: 'No. commande d\'achat', key: 'noCommandeAchat', width: '60px'},
  { title: 'Client', key: 'client', width: '120px'},
];
const totalUnités = computed(() =>
  buckets.value.reduce((sum, b) => sum + (b.unités || 0), 0)
)
const totalPalettes = computed(() =>
  buckets.value.reduce((sum, b) => sum + (b.nombrePalette || 0), 0)
)

watch(selectedDay, (_) => getData())

async function getData() {
  try{
    if (isLoading.value) {
      return
    }
    isLoading.value = true

    storeU.journéeSélectionnée = selectedDay.value.toISOString()
    buckets.value = []

    if (selectedSite.value == 'VGCO') {
      selectedSite.value = 'Tous'
    }

    const siteCS = selectedSite.value == 'Tous' ? 'VGCO' : selectedSite.value
    const siteId = await storeU.getOrFetchSiteId(siteCS)

    await storeU.fetchVegcoProduitsIfNeeded()

    const minDate = new Date(selectedDay.value)
    minDate.setDate(minDate.getDate() - 5)
    const maxDate = new Date(selectedDay.value)
    maxDate.setDate(maxDate.getDate() + 5)
    await storeVegco.loadDaysMultipleCultures(minDate, maxDate, [
      'Iceberg / Pommée', 'Laitue Romaine', 'Laitue Feuille Verte',
      'Laitue Feuille Rouge'
    ])

    const bucketsFound = storeVegco.getStoredBuckets(selectedDay.value)
    for (const bucket of bucketsFound) {
      if (bucket.quantité > 0 &&
        bucket.siteId == siteId || bucket.siteGroupeId == siteId) {

        if (bucket.commandeVenteId == emptyMongoId()) {
          continue // Skip buckets without a vente command
        }

        // Avoid duplicates
        if (buckets.value.find(b2 => b2.id == bucket.id)) {
          continue
        }

        const b = { ...bucket } as MDBVegcoBucketExtended

        b.producteur = storeVegco.getSiteLettresDappelRenommé(bucket.siteId) ?? '?'
        b.publié = new Date(b.datePublication).getFullYear() > 2000

        const produit = storeU.getVegcoProduit(bucket.codeProduit)
        if (produit) {
          b.nomProduit = produit.description
          b.typePalette = produit.typePalette
          b.unités = roundTo(b.quantité / produit.quantité, 1)
        } else {
          const units = unitésParBoîte(bucket.culture)
          b.nomProduit = getBucketBigType(bucket)
          b.unités = roundTo(b.quantité / units, 1)
        }

        const commande = storeVegco.commandesStore.find(c => c.id == bucket.commandeVenteId)
        if (commande) {
          b.noCommandeVente = commande.noCommande
          b.client = commande.client
          b.dateChargé = new Date(commande.dateChargé)
          b.dateChargéF = to_wd_dd_MMMM_hh_mm(b.dateChargé)

          const détail = commande.détails.find(d => d.codeProduit == bucket.codeProduit)
          if (détail) {
            b.nombrePalette = b.quantité / détail.quantité * détail.nombrePalette
            const nbPaletteArrondi = roundTo(b.nombrePalette, 0)
            if (Math.abs(nbPaletteArrondi - b.nombrePalette) <= 0.1) {
              b.nombrePalette = nbPaletteArrondi
            }

            b.unitsParPalette = détail.nombrePalette == 0 ? 0 : roundTo(b.unités / b.nombrePalette, 1)
          }
        }

        buckets.value.push(b)
      }
    }

    newData.value = false
  }
  catch (e) {
    notifyError(e)
  }
  finally {
    isLoading.value = false
  }
}

const dialogBucket = useTemplateRef('dialogBucket')
async function openBucket(bucket: MDBVegcoBucket) {
  const b = storeVegco.bucketsStore.find(b => b.id == bucket.id)
  if (!b) {
    notifyError('Bucket introuvable')
    return
  }
  const modifs = await dialogBucket.value?.openBucketEdit(b, selectedDay.value)
  if (modifs)
    getData()
}

const intervalId = setInterval(async () => {
  if (await storeVegco.isRevisionChanged())
    newData.value = true
}, 3000)

onMounted(async () => getData())

onUnmounted(() => {
  if (intervalId)
    clearInterval(intervalId)
})
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

        <!-- Left: title -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="M23,4.1V2.3L21.2,2.1C21.1,2.1 20.5,2 19.5,2C15.4,2 12.4,3.2 10.7,5.3C9.4,4.5 7.6,4 5.5,4C4.5,4 3.8,4.1 3.8,4.1L1.9,4.4L2,6.1C2.1,9.1 3.6,14.8 8.8,14.8C8.9,14.8 8.9,14.8 9,14.8V18.2C5.2,18.7 2,20 2,20V22H22V20C22,20 18.8,18.7 15,18.2V15C21.3,14.9 23,7.8 23,4.1M12,18C11.7,18 11.3,18 11,18V12.4C11,12.4 10.8,9 8,9C8,9 9.5,9.8 9.9,12.7C9.5,12.8 9.1,12.8 8.8,12.8C4.2,12.8 4,6.1 4,6.1C4,6.1 4.6,6 5.5,6C7.4,6 10.5,6.4 11.4,9.1C11.9,4.6 17,4 19.5,4C20.4,4 21,4.1 21,4.1C21,4.1 21,13.1 14.7,13.1C14.5,13.1 14.2,13.1 14,13.1C14,11.1 16,8.1 16,8.1C13,9.1 13,13 13,13V18C12.7,18 12.3,18 12,18Z" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Récoltes par jour</div>
            <div class="text-caption text-medium-emphasis">Buckets par date de chargement</div>
          </div>
        </div>

        <!-- Right: actions -->
        <div class="d-flex align-center" style="gap: 8px;">
          <span class="text-body-2 text-medium-emphasis">Attention : cette liste provient des buckets. Vérifiez les POs réels.</span>
          <v-btn v-if="newData" color="primary" variant="text" prepend-icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" @click="getData()">Recharger les données</v-btn>
        </div>
      </div>

      <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px;">
        <DaySelector ref="daySelector" v-model="selectedDay"/>
        <v-spacer/>
        <v-select
          v-model="selectedSite"
          :items="sites"
          label="Site"
          density="compact" variant="outlined" rounded="lg" hide-details
          style="max-width: 300px;"
          @update:model-value="getData()"/>
      </div>
    </div>
    <v-divider/>

    <v-data-table
      :headers="headers" :items="buckets"
      :loading="isLoading" item-key="id"
      :sort-by="[{ key: 'dateChargé', order: 'asc' }]" :sort-desc="false"
      :items-per-page="-1"
      :hide-default-header="$vuetify.display.smAndDown">
      <template #item.dateChargé="props">
        <v-chip :text="props.item.dateChargéF"/>
      </template>
      <template #item.noCommandeAchat="props">
        <v-btn
          size="small" color="primary"
          class="ma-1" @click="openBucket(props.item)">
          {{ props.item.noCommandeAchat }}
        </v-btn>
        <span>
          <v-icon v-if="props.item.publié" small class="ml-1">M12,19.2C9.5,19.2 7.29,17.92 6,16C6.03,14 10,12.9 12,12.9C14,12.9 17.97,14 18,16C16.71,17.92 14.5,19.2 12,19.2M12,5A3,3 0 0,1 15,8A3,3 0 0,1 12,11A3,3 0 0,1 9,8A3,3 0 0,1 12,5M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12C22,6.47 17.5,2 12,2Z</v-icon>
          <v-tooltip activator="parent" location="bottom">Publié</v-tooltip>
        </span>
      </template>
      <template v-if="$vuetify.display.smAndDown" #item="props">
        <tr>
          <td class="pt-2 pb-2">
            <v-container>
              <v-row>
                <v-col cols="8">
                  Producteur: {{ props.item.producteur }}<br/>
                  Nom produit: {{ props.item.nomProduit }}<br/>
                  Code de produit: {{ props.item.codeProduit }}<br/>
                  Type de palette: {{ props.item.typePalette }}<br/>
                  Nb unités / palette: {{ props.item.unitsParPalette }}<br/>
                  Nb de palettes: {{ props.item.nombrePalette }}<br/>
                  Unités: {{ props.item.unités }}<br/>
                  Heure shipping MF: {{ props.item.dateChargéF }}<br/>
                  # de commande d'achat: {{ props.item.noCommandeAchat }}<br/>
                  # de commande de vente: {{ props.item.noCommandeVente }}<br/>
                  Client: {{ props.item.client }}<br/>
                </v-col>
              </v-row>
            </v-container>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-sheet class="pa-2 d-flex justify-space-between" style="border-top: 1px solid rgba(0,0,0,0.12);">
      <div class="font-weight-bold">Total</div>
      <div class="d-flex">
        <div class="px-4 font-weight-bold">Palettes: {{ totalPalettes }}</div>
        <div class="px-4 font-weight-bold">Unités: {{ totalUnités }}</div>
      </div>
    </v-sheet>

  </v-card>

  <DialogBucket ref="dialogBucket"/>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}
</style>
